import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BaseProjectComponent } from '../../../../components/base-project.component';
import { Countries } from '@shared/interfaces/account/countries.interface';
import { Response } from '@shared/models/response.model';
import { User } from '@shared/models/user/user.model';
import { Error } from '@shared/interfaces/error.interface';
import { Config } from '@shared/config';
import { PhInputComponent } from '@shared/components/ph-input/ph-input.component';
import { Day, Month, Year } from '@shared/interfaces/datetime.interface';
import { CartService } from '@layouts/default/layouts/logged/pages/cart/cart.service';
import { AppInjector } from '@shared/services/app-injector.service';
import { PayReq } from '@shared/interfaces/cart/pay-req.interface';
import { BehaviorSubject, Subject } from 'rxjs';

@Component({
  selector: 'app-additional-info',
  templateUrl: './additional-info.component.html',
  styleUrls: ['./additional-info.component.scss'],
})
export class AdditionalInfoComponent
  extends BaseProjectComponent
  implements OnInit, AfterViewChecked
{
  cartService: CartService;
  form: UntypedFormGroup = new UntypedFormGroup({});
  days: Day[] = [];
  months: Month[] = [];
  years: Year[] = [];
  countries: Countries[] = [];
  @Input() payment: any;
  @Input() paymentType: string;
  @Output() additionalSuccess: EventEmitter<any> = new EventEmitter();
  @Output() additionalClose: EventEmitter<any> = new EventEmitter();
  paypalTerms: string = Config.PAYPAL_TERMS;
  displayConfirmDeleteModal: boolean = false;
  @ViewChild('confirmDelete') confirmDelete: PhInputComponent;
  displayDeleteModal: boolean = false;
  formConfirmAccountDelete: UntypedFormGroup = new UntypedFormGroup({});
  formConfirmAccountDeleteIsInvalid$: Subject<boolean> = new BehaviorSubject(true);
  displayAdditionalInfoModal: boolean = false;
  disabled: boolean = false;
  mailNotifications: boolean = false;

  @HostListener('window:beforeunload', ['$event'])
  public checkData() {
    if (this.displayAdditionalInfoModal) {
      return false;
    }
  }

  constructor(private cr: ChangeDetectorRef) {
    super();
    const injector = AppInjector.getInjector();
    this.cartService = injector.get(CartService);
    this.getCountriesList();
    this.getCountriesList();
    const sub = this.languageService.langChangeEmitter.subscribe(() => {
      this.countries = [];
      this.getCountriesList();
    });
    this.someSubscriptions.add(sub);
    this.days = this.datetime.setDays('1', 1920);
    this.months = this.datetime.setMonths();
    this.years = this.datetime.setYears();
  }

  ngOnInit() {
    setTimeout(() => this.setFormData(), 100);
  }

  ngAfterViewChecked() {
    this.cr.detectChanges();
  }

  checkFormConfirmAccountDeleteIsInvalid() {
    this.formConfirmAccountDeleteIsInvalid$.next(this.formConfirmAccountDelete.invalid);
  }

  setFormData() {
    const formData = JSON.parse(localStorage.getItem('@add-info'));
    if (formData) {
      Object.keys(formData).forEach((key) => {
        const val = this.storage.user[key] ? this.storage.user[key] : formData[key];
        const formControl = this.form.get(key);
        !!formControl && formControl.patchValue(val, { emitEvent: false });
      });
      localStorage.removeItem('@add-info');
    }
  }

  openDeleteAccountIfHaveProject() {
    if (this.storage.user) {
      if (
        this.storage.user.settings.payment_terms &&
        (this.storage.user.basic_projects_info.active > 0 ||
          this.storage.user.basic_projects_info.closed > 0 ||
          this.storage.user.basic_projects_info.in_verification > 0)
      ) {
        this.displayDeleteModal = true;
      }
    }
  }

  toggleMailNotif() {
    this.mailNotifications = !this.mailNotifications;
  }

  onYearSelect(event) {
    this.onMonthSelect(
      this.form.get('month').value ? this.form.get('month').value : 1,
      event.value
    );
  }

  onMonthSelect(event, year?) {
    const month = typeof event === 'number' ? event : event.value;
    const getDay = this.form.get('day');
    const selectedDay = getDay.value;
    if (month === 2) {
      this.days = this.datetime.setDays(month ? month : 1, year);
      getDay.patchValue(this.days, { emitEvents: true });
    } else {
      this.days = this.datetime.setDays(month ? month : 1, year);
      getDay.patchValue(this.days, { emitEvents: true });
    }
    if (selectedDay > this.days[this.days.length - 1].value) {
      this.form.get('day').patchValue(this.days, { emitEvents: true });
    } else {
      this.form.get('day').patchValue(selectedDay, { emitEvents: true });
    }
  }

  getCountriesList() {
    const subCountries = this.accountService
      .countriesList()
      .subscribe((res: Response<Countries[]>) => {
        this.countries = res.data.map((el) => {
          return {
            code: el.code,
            continent: el.continent,
            name: this.translate.instant('COUNTRIES.' + el.code.toUpperCase()),
          };
        });
      }, this.onCountriesListError.bind(this));
    this.someSubscriptions.add(subCountries);
  }

  deleteAccount() {
    this.loading = true;
    const pack = this.formConfirmAccountDelete.value;
    const subDeleteAccount = this.accountService
      .deleteAccount(pack)
      .subscribe((res: Response<User>) => {
        this.displayDeleteModal = false;
        this.loading = false;
        this.toast.success(
          this.translate.instant('ACCOUNT.user_delete'),
          this.translate.instant('ACCOUNT.user_success')
        );
        this.storage.logout();
      }, this.onDeleteAccountError.bind(this));
    this.someSubscriptions.add(subDeleteAccount);
  }

  get socialUserInfo() {
    return this.storage.user && (this.storage.user.facebook_id || this.storage.user.googleplus_id);
  }

  onDeleteModal() {
    this.form.get('payment_terms').setValue(true);
  }

  onDeleteAccountError(error: Error) {
    this.loading = false;
    switch (error?.message) {
      case 'user_exists': {
        this.toast.error(
          this.translate.instant('ACCOUNT.user_cant_delete'),
          this.translate.instant('ACCOUNT.user_error')
        );
        break;
      }
      case 'invalid_password': {
        this.toast.error(
          this.translate.instant('ACCOUNT.user_pass_not_correct'),
          this.translate.instant('ACCOUNT.user_error')
        );
        break;
      }
      case 'cannot_delete_account_projects_active': {
        this.toast.error(
          this.translate.instant('ACCOUNT.cannot_delete_account_projects_active'),
          this.translate.instant('ACCOUNT.user_error')
        );
        break;
      }
    }
  }

  onCountriesListError(error: Error) {
    this.toast.error(
      this.translate.instant('ACCOUNT.countries_cant_get'),
      this.translate.instant('ACCOUNT.countries_error')
    );
  }

  shouldShowInput(inputName: string) {
    const { user } = this.storage;
    const isLoggedIn = !!user;
    const isUserLegal = isLoggedIn && user.type === 'legal';
    const userHasName = isLoggedIn && user.name && user.surname;
    const isPayPal = this.paymentType === 'paypal';

    switch (inputName) {
      case 'profile_name':
        return !isLoggedIn || !isUserLegal;
      case 'your_email':
        return !isLoggedIn;
      case 'profile_country':
      case 'personal_information':
      case 'payment_terms':
      case 'birthday':
        return isLoggedIn;
    }
  }

  get userBirthYear() {
    return parseInt(this.datetime.birthYear, 10);
  }

  get userBirthMonth() {
    return parseInt(this.datetime.birthMonth, 10);
  }

  get userBirthDay() {
    return parseInt(this.datetime.birthDay, 10);
  }

  updateProfile() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.disabled = true;
    this.loading = true;
    const { payment_terms, ...othersUserInfo } = this.form.value;
    const pack: User = { ...othersUserInfo, settings: { payment_terms } };

    if (this.storage.user && this.form.get('day') && this.form.get('month') && this.form.get('year')) {
      pack.birthday = this.datetime.formatDate(
        this.form.get('day').value,
        this.form.get('month').value,
        this.form.get('year').value
      );
      delete pack.day;
      delete pack.month;
      delete pack.year;
    }
    if (this.storage.user) {
      const subChangeUser = this.accountService
        .changeUser(pack, this.storage.user.id)
        .subscribe((res: Response<User>) => {
          const user: User = res.data;
          // recheck if all fields was be added (backend not send error if you forget about, for example, field 'name')
          if (this.checkUserHaveAllInfo(user)) {
            this.onSaveProfileError({ message: 'not all added', expection: 'not all added' });
            return;
          }
          this.storage.setUser(user);
          this.loading = false;
          this.toast.success(
            this.translate.instant('ACCOUNT.profile_saved'),
            this.translate.instant('ACCOUNT.profile_success')
          );
          this.disabled = false;
          this.additionalSuccess.emit(true);
          this.displayAdditionalInfoModal = false;
        }, this.onSaveProfileError.bind(this));
      this.someSubscriptions.add(subChangeUser);
    } else {
      this.payUnregistered(pack);
    }
  }

  payUnregistered(pack) {
    pack.payment_terms = pack.settings.payment_terms;
    pack.mail_notifications = this.mailNotifications;
    delete pack.settings;
    delete pack.tax_residence;
    const payload = {
      user: pack,
      payment: this.payment,
      is_anonymous: this.payment.is_anonymous,
    };

    switch (this.paymentType) {
      case 'tpay':
        const tpayPayload = {
          amount: this.payment.amount,
          payment_channel: this.payment.payment_type,
          is_anonymous: this.payment.is_anonymous,
          success_url: null,
          error_url: null,
          user: pack,
        };
        const payAndCreateTpay = this.cartService
          .payTpay(tpayPayload, this.payment.project_id)
          .subscribe((el: Response<PayReq>) => {
            this.loading = false;
            this.additionalSuccess.emit(true);
            this.displayAdditionalInfoModal = false;
            el.data.token && localStorage.setItem('token', el.data.token);
            localStorage.setItem('email', pack.email);
            window.location.href = el.data.url;
          }, this.onPayAndCreateError.bind(this));
        this.someSubscriptions.add(payAndCreateTpay);
        break;
      case 'paypal':
        const payPalPayload = {
          amount: this.payment.amount,
          is_anonymous: this.payment.is_anonymous,
          user: pack,
        };
        const payAndCreatePayPal = this.cartService
          .payPayPal(payPalPayload, this.payment.project_id)
          .subscribe((el: Response<PayReq>) => {
            this.loading = false;
            this.additionalSuccess.emit(true);
            this.displayAdditionalInfoModal = false;
            el.data.token && localStorage.setItem('token', el.data.token);
            localStorage.setItem('email', pack.email);
            window.location.href = el.data.url;
          }, this.onPayAndCreateError.bind(this));
        this.someSubscriptions.add(payAndCreatePayPal);
        break;
    }
  }

  get isLegal() {
    return this.storage.user.type === 'legal';
  }

  onSaveProfileError(error: Error) {
    this.loading = false;
    this.disabled = false;
    this.toast.error(
      this.translate.instant('ACCOUNT.profile_not_saved'),
      this.translate.instant('ACCOUNT.profile_error')
    );
  }

  onPayAndCreateError(error: Error) {
    this.loading = false;
    switch (error?.message) {
      case 'cart_is_empty': {
        this.toast.error(
          this.translate.instant('PAYMENT.cart_is_empty'),
          this.translate.instant('PAYMENT.payment_error')
        );
        break;
      }
      case 'bad_money_amount': {
        this.toast.error(
          this.translate.instant('PAYMENT.bad_amount'),
          this.translate.instant('PAYMENT.payment_error')
        );
        break;
      }
      case 'payment_email_already_exists': {
        this.onUserExists();
        this.toast.error(
          this.translate.instant('PAYMENT.cannot_find_user_mango_account_pay'),
          this.translate.instant('PAYMENT.payment_error')
        );
        break;
      }
      default: {
        this.toast.error(
          this.translate.instant('PAYMENT.cant_send_pay'),
          this.translate.instant('PAYMENT.payment_error')
        );
      }
    }
  }

  onUserExists() {
    const formData = this.form.getRawValue();
    localStorage.setItem('@add-info', JSON.stringify(formData));
    localStorage.setItem('@should-open-add-info', 'true');
    this.closeModal();
    this.router.navigate([], { queryParams: this.authPage('login', { disableRedirect: true }) });
  }

  closeModal() {
    this.displayAdditionalInfoModal = false;
    this.additionalClose.emit();
  }
}
